// Screenshot capture for the feedback reporter. Two sources, no third-party library:
//
//   1. captureScreen()  — the browser's own screen capture (getDisplayMedia). The viewer
//      picks what to share (Chrome/Edge offer the current tab first via preferCurrentTab),
//      we grab ONE frame and stop the track immediately. Nothing is recorded or streamed.
//   2. imageFromPaste()/imageFromFiles() — a screenshot they took themselves, pasted or picked.
//
// Everything below is browser-capability-guarded: the caller renders the capture button only
// when canCaptureScreen() is true, and falls back to attach/paste elsewhere (Safari, iOS,
// an insecure origin, or a policy that blocks display capture).

/** Does this browser offer screen capture on this origin? (Needs a secure context.) */
export function canCaptureScreen(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === 'function';
}

/** Thrown when the viewer dismisses the browser's own "choose what to share" prompt. */
export class CaptureCancelled extends Error {
  constructor() {
    super('Screen capture was cancelled');
    this.name = 'CaptureCancelled';
  }
}

function stamp(): string {
  return new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
}

async function frameFromStream(stream: MediaStream): Promise<Blob> {
  const track = stream.getVideoTracks()[0];
  if (!track) throw new Error('The capture returned no video track');
  try {
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    await video.play();
    // One rAF: the first frame is not painted the instant play() resolves.
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    const settings = track.getSettings();
    const canvas = document.createElement('canvas');
    canvas.width = settings.width ?? video.videoWidth;
    canvas.height = settings.height ?? video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('This browser could not render the captured frame');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    video.pause();
    video.srcObject = null;
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) throw new Error('This browser could not encode the captured frame');
    return blob;
  } finally {
    // Always release the camera-bar / "sharing" indicator, success or failure.
    for (const t of stream.getTracks()) t.stop();
  }
}

/**
 * Capture one frame of what the viewer chooses to share. `hide` is awaited before the
 * picker opens and called again to restore — the reporter's dialog gets out of its own shot.
 */
export async function captureScreen(hide?: { before: () => void; after: () => void }): Promise<File> {
  if (!canCaptureScreen()) throw new Error('This browser cannot capture the screen');
  hide?.before();
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({
      // preferCurrentTab is Chromium-only and simply ignored elsewhere.
      video: { displaySurface: 'browser' },
      audio: false,
      preferCurrentTab: true,
      selfBrowserSurface: 'include',
      surfaceSwitching: 'exclude',
    } as DisplayMediaStreamOptions);
  } catch (err) {
    // NotAllowedError is the viewer closing the picker — not an error worth a red toast.
    if (err instanceof DOMException && (err.name === 'NotAllowedError' || err.name === 'AbortError')) throw new CaptureCancelled();
    throw err;
  } finally {
    hide?.after();
  }
  const blob = await frameFromStream(stream);
  return new File([blob], `screenshot-${stamp()}.png`, { type: 'image/png' });
}

/** The image on a paste event, if there is one (Print Screen → Ctrl+V is how most people do it). */
export function imageFromPaste(e: ClipboardEvent): File | null {
  const items = Array.from(e.clipboardData?.items ?? []);
  for (const item of items) {
    if (item.kind !== 'file' || !item.type.startsWith('image/')) continue;
    const file = item.getAsFile();
    if (!file) continue;
    // A pasted bitmap has no name; give it one so the attachment list reads sensibly.
    return file.name && file.name !== 'image.png' ? file : new File([file], `pasted-${stamp()}.png`, { type: file.type });
  }
  return null;
}

/** The images among a FileList / drop (anything that is not an image is ignored). */
export function imageFromFiles(list: FileList | File[] | null): File[] {
  return Array.from(list ?? []).filter((f) => f.type.startsWith('image/'));
}
